/**
 * Centralized Z-Index Configuration
 *
 * This file maintains a single source of truth for all z-index values across the application.
 * The hierarchy is designed to ensure proper layering of UI elements with clear semantic gaps.
 *
 * Z-Index Hierarchy (lowest to highest):
 * - Popovers/Tooltips: 1201
 * - Side Panel Modals: 1250-1251 (overlay < content)
 * - Dialogs: 1260-1261 (overlay < content)
 * - MUI Modals: 1350
 * - Toast Notifications: 1400 (always on top)
 */

export const Z_INDEX = {
  /**
   * Combobox dropdown layer
   * Used by: Combobox, GroupCombobox components
   * Must be below all modal layers
   */
  COMBOBOX: 1201,

  /**
   * Side Panel Modal - Overlay/Backdrop
   * The dark background overlay for side panel modals
   * Must be below SIDE_PANEL_CONTENT
   */
  SIDE_PANEL_OVERLAY: 1250,

  /**
   * Side Panel Modal - Content
   * The actual panel content that slides in from the side
   * Must be above SIDE_PANEL_OVERLAY
   */
  SIDE_PANEL_CONTENT: 1251,

  /**
   * Dialog - Overlay/Backdrop
   * The dark background overlay for dialog modals
   * Must be below DIALOG_CONTENT
   */
  DIALOG_OVERLAY: 1260,

  /**
   * Dialog - Content
   * The actual dialog content
   * Must be above DIALOG_OVERLAY
   */
  DIALOG_CONTENT: 1261,

  /**
   * MUI Modal layer
   * Used by Material-UI based modals (AppModal, etc.)
   * Elevated above side panels and dialogs
   */
  MUI_MODAL: 1350,

  /**
   * Toast Notification layer (highest)
   * Used by Toast/Snackbar components for error messages, success messages, etc.
   * MUST always be on top of all other layers to ensure notifications are visible
   */
  TOAST_NOTIFICATION: 9999,
} as const;

/**
 * Type for z-index constant keys
 */
export type ZIndexKey = keyof typeof Z_INDEX;

/**
 * Type for z-index values
 */
export type ZIndexValue = (typeof Z_INDEX)[ZIndexKey];
